import {
  ApplicationType,
  NewReleaseNotificationDataTypeEnum,
  ReleaseNotificationResponse,
} from "@veris-health/communication-ms/lib/v1";
import {
  CreatePatientRequest,
  CreatePatientResponse,
  CreateAdminTagsRequest,
  TagItem,
  UpdateAdminTagRequest,
  SuccessfulResponse,
} from "@veris-health/user-ms/lib/v1";
import {
  notificationsApiV1,
  registerApiV1,
  usersApiV1,
  adminTagsApiV1,
  hospitalsApiV1,
} from "../../../api";
import axiosInstance from "../../../api/axios-config";
import { API_ROUTES, getVersion, VERSIONS } from "../../../api/helpers";

export const sendReleaseNotification = async (
  link: string,
  version: string,
  appType: ApplicationType,
): Promise<ReleaseNotificationResponse> => {
  const response = await notificationsApiV1.sendReleaseNotifications({
    app_type: appType,
    link,
    version,
    type: NewReleaseNotificationDataTypeEnum.ReleaseNotes,
  });
  return response.data;
};

export const invitePatient = async (data: CreatePatientRequest): Promise<CreatePatientResponse> => {
  const response = await usersApiV1.createPatient(data);
  return response.data;
};

export const registerPatientsFromCSV = async (
  hospitalId: number,
  file: File,
): Promise<SuccessfulResponse> => {
  const response = await registerApiV1.bulkRegisterPatientUsersCsv(hospitalId, file);
  return response.data;
};

export const createTags = async (data: CreateAdminTagsRequest): Promise<TagItem> => {
  const response = await adminTagsApiV1.adminCreateTags(data);
  return response.data;
};

export const editTag = async (
  tagId: number,
  updatedName: UpdateAdminTagRequest,
): Promise<TagItem> => {
  const response = await adminTagsApiV1.adminUpdateTag(tagId, updatedName);
  return response.data;
};

export const deleteTag = async (tagId: number): Promise<void> => {
  const response = await adminTagsApiV1.adminDeleteTag(tagId);
  return response.data;
};

export const createHospital = async (name: string): Promise<void> => {
  await hospitalsApiV1.createHospital({ name });
};

// Integration Client types and API
export interface IntegrationClientDefaultThresholds {
  // eslint-disable-next-line camelcase
  default_min_temp?: number | null;
  // eslint-disable-next-line camelcase
  default_max_temp?: number | null;
  // eslint-disable-next-line camelcase
  default_min_sys?: number | null;
  // eslint-disable-next-line camelcase
  default_max_sys?: number | null;
  // eslint-disable-next-line camelcase
  default_min_dia?: number | null;
  // eslint-disable-next-line camelcase
  default_max_dia?: number | null;
  // eslint-disable-next-line camelcase
  default_min_hr?: number | null;
  // eslint-disable-next-line camelcase
  default_max_hr?: number | null;
  // eslint-disable-next-line camelcase
  default_min_rpm?: number | null;
  // eslint-disable-next-line camelcase
  default_max_rpm?: number | null;
  // eslint-disable-next-line camelcase
  default_min_so2?: number | null;
  // eslint-disable-next-line camelcase
  default_min_steps?: number | null;
}

export interface CreateIntegrationClientRequest {
  name: string;
  // eslint-disable-next-line camelcase
  hospital_id: number;
  provider: "rhapsody" | "redox";
  // eslint-disable-next-line camelcase
  main_oncologist_id?: number;
  // eslint-disable-next-line camelcase
  outbound_endpoint?: string;
  // eslint-disable-next-line camelcase
  outbound_secret_reference?: string;
  // eslint-disable-next-line camelcase
  default_thresholds?: IntegrationClientDefaultThresholds;
}

export interface CreateIntegrationClientResponse {
  id: number;
  name: string;
  provider: string;
  // eslint-disable-next-line camelcase
  api_key: string;
}

export const createIntegrationClient = async (
  data: CreateIntegrationClientRequest,
): Promise<CreateIntegrationClientResponse> => {
  const response = await axiosInstance.post<CreateIntegrationClientResponse>(
    `/${getVersion(API_ROUTES.MED_DATA, VERSIONS.v1)}/admin/integration-clients`,
    data,
  );
  return response.data;
};

// --- Edit Integration Client types and API ---

export interface IntegrationClientSummary {
  id: number;
  name: string;
  provider: string;
  // eslint-disable-next-line camelcase
  hospital_id: number | null;
  // eslint-disable-next-line camelcase
  is_active: boolean;
}

export interface ListIntegrationClientsResponse {
  clients: IntegrationClientSummary[];
}

export interface IntegrationClientDetail {
  id: number;
  name: string;
  provider: string;
  // eslint-disable-next-line camelcase
  hospital_id: number | null;
  // eslint-disable-next-line camelcase
  is_active: boolean;
  // eslint-disable-next-line camelcase
  outbound_endpoint: string | null;
  // eslint-disable-next-line camelcase
  outbound_secret_reference: string | null;
  // eslint-disable-next-line camelcase
  main_oncologist_id: number | null;
  // eslint-disable-next-line camelcase
  default_thresholds?: IntegrationClientDefaultThresholds | null;
}

export interface UpdateIntegrationClientRequest {
  // eslint-disable-next-line camelcase
  outbound_endpoint?: string | null;
  // eslint-disable-next-line camelcase
  outbound_secret_reference?: string | null;
  // eslint-disable-next-line camelcase
  main_oncologist_id?: number | null;
  // eslint-disable-next-line camelcase
  default_thresholds?: IntegrationClientDefaultThresholds | null;
}

export interface RotateApiKeyRequest {
  // eslint-disable-next-line camelcase
  rotate_to: "primary" | "secondary";
}

export interface RotateApiKeyResponse {
  // eslint-disable-next-line camelcase
  api_key: string;
  // eslint-disable-next-line camelcase
  rotated_slot: string;
}

export const listIntegrationClients = async (): Promise<ListIntegrationClientsResponse> => {
  const response = await axiosInstance.get<ListIntegrationClientsResponse>(
    `/${getVersion(API_ROUTES.MED_DATA, VERSIONS.v1)}/admin/integration-clients`,
  );
  return response.data;
};

export const getIntegrationClient = async (
  clientId: number,
): Promise<IntegrationClientDetail> => {
  const response = await axiosInstance.get<IntegrationClientDetail>(
    `/${getVersion(API_ROUTES.MED_DATA, VERSIONS.v1)}/admin/integration-clients/${clientId}`,
  );
  return response.data;
};

export const updateIntegrationClient = async (
  clientId: number,
  data: UpdateIntegrationClientRequest,
): Promise<IntegrationClientDetail> => {
  const response = await axiosInstance.put<IntegrationClientDetail>(
    `/${getVersion(API_ROUTES.MED_DATA, VERSIONS.v1)}/admin/integration-clients/${clientId}`,
    data,
  );
  return response.data;
};

export const rotateIntegrationClientKey = async (
  clientId: number,
  data: RotateApiKeyRequest,
): Promise<RotateApiKeyResponse> => {
  const response = await axiosInstance.post<RotateApiKeyResponse>(
    `/${getVersion(API_ROUTES.MED_DATA, VERSIONS.v1)}/admin/integration-clients/${clientId}/rotate-key`,
    data,
  );
  return response.data;
};
